import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, CheckCircle2, XCircle, Clock, UserCheck, AlertTriangle, Loader2,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * P6 — PO Approvals timeline + level approvals.
 * URL: /admin/procurement/purchase-orders/:poId/approvals
 */

const LEVEL_META = {
  procurement: { label: 'Procurement', color: 'bg-sky-100 text-sky-700' },
  finance:     { label: 'Finance',     color: 'bg-emerald-100 text-emerald-700' },
  management:  { label: 'Management',  color: 'bg-violet-100 text-violet-700' },
  executive:   { label: 'Executive',   color: 'bg-rose-100 text-rose-700' },
};


const POApprovals = () => {
  const { poId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = useMemo(() => localStorage.getItem('admin_token'), []);
  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [po, setPo] = useState(null);
  const [requiredLevels, setRequiredLevels] = useState([]);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [poRes, reqRes, stepsRes] = await Promise.all([
        axios.get(`${API_URL}/api/procurement/purchase-orders/${poId}`, authHeader),
        axios.get(`${API_URL}/api/procurement/po-approvals/${poId}/required-levels`, authHeader),
        axios.get(`${API_URL}/api/procurement/po-approvals/${poId}/steps`, authHeader),
      ]);
      setPo(poRes.data);
      setRequiredLevels(reqRes.data?.required_levels || []);
      setSteps(stepsRes.data || []);
    } catch (err) {
      toast({ title: 'Load failed', description: err?.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [poId, authHeader, toast]);

  useEffect(() => { load(); }, [load]);

  const approve = useCallback(async (level) => {
    setBusy(`approve-${level}`);
    try {
      const r = await axios.post(`${API_URL}/api/procurement/purchase-orders/${poId}/approve`,
        { level }, authHeader);
      toast({
        title: r.data.is_fully_approved ? 'PO fully approved' : `${level} approval recorded`,
        description: `Required: ${r.data.required.join(' → ')}`,
      });
      await load();
    } catch (err) {
      toast({ title: 'Approval failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setBusy(null); }
  }, [poId, authHeader, toast, load]);

  const reject = useCallback(async (level) => {
    const reason = window.prompt('Rejection reason?'); // eslint-disable-line no-alert
    if (!reason) return;
    setBusy(`reject-${level}`);
    try {
      await axios.post(`${API_URL}/api/procurement/po-approvals/${poId}/reject`,
        { level, reason }, authHeader);
      toast({ title: 'PO rejected', description: reason });
      await load();
    } catch (err) {
      toast({ title: 'Rejection failed', description: err?.message, variant: 'destructive' });
    } finally { setBusy(null); }
  }, [poId, authHeader, toast, load]);

  const stepByLevel = useMemo(() => {
    const map = {};
    steps.forEach((s) => { map[s.level] = s; });
    return map;
  }, [steps]);

  if (loading || !po) {
    return <div className="p-6 flex items-center gap-2 text-slate-500" data-testid="approvals-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading approvals…</div>;
  }

  const rejected = po.status === 'rejected';
  const fullyApproved = po.status === 'approved';

  return (
    <div data-testid="po-approvals-page">
      <button onClick={() => navigate(-1)} className="text-xs text-[#6C757D] hover:text-[#023E8A] flex items-center gap-1 mb-3" data-testid="approvals-back">
        <ArrowLeft className="w-3 h-3" /> Back
      </button>

      <div className="mb-5">
        <h1 className="text-2xl font-bold text-[#2B2D42]">PO Approvals</h1>
        <p className="text-sm text-[#6C757D]">
          <span className="font-mono">{po.po_number}</span> · Supplier: {po.supplier_snapshot?.name || po.supplier_id} · Total: {po.currency} {Number(po.total_amount).toLocaleString()}
        </p>
        <span className={`inline-block mt-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
          rejected ? 'bg-rose-100 text-rose-700'
          : fullyApproved ? 'bg-emerald-100 text-emerald-700'
          : 'bg-amber-100 text-amber-700'
        }`} data-testid="approvals-status-badge">{po.status}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2" data-testid="approvals-flow-card">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold uppercase tracking-wide text-[#2B2D42] mb-4">Approval Flow</h3>
            <div className="space-y-3">
              {requiredLevels.length === 0 ? (
                <p className="text-xs text-[#6C757D]">No approval levels required by policy for this amount.</p>
              ) : requiredLevels.map((level, idx) => {
                const step = stepByLevel[level];
                const meta = LEVEL_META[level] || { label: level, color: 'bg-slate-100 text-slate-700' };
                const isApproved = step?.decision === 'approved';
                const isRejected = step?.decision === 'rejected';
                const isPending = !step;
                return (
                  <div key={level} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200" data-testid={`approvals-level-${level}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isApproved ? 'bg-emerald-100 text-emerald-700'
                      : isRejected ? 'bg-rose-100 text-rose-700'
                      : 'bg-slate-100 text-slate-500'
                    }`}>
                      {isApproved ? <CheckCircle2 className="w-4 h-4" /> : isRejected ? <XCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-bold text-slate-400">{String(idx + 1).padStart(2, '0')}</span>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${meta.color}`}>
                          {meta.label}
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-[#2B2D42] mt-0.5 capitalize">
                        {isApproved ? 'Approved' : isRejected ? 'Rejected' : 'Pending'}
                      </div>
                      {step && (
                        <div className="text-[11px] text-[#6C757D] mt-1">
                          By {step.decided_by || '—'} · {(step.decided_at || '').slice(0, 19).replace('T', ' ')}
                          {step.note && <span className="block italic">"{step.note}"</span>}
                        </div>
                      )}
                    </div>
                    {isPending && !rejected && !fullyApproved && (
                      <div className="flex flex-col gap-1 items-end">
                        <Button size="sm" onClick={() => approve(level)} disabled={busy === `approve-${level}`}
                                data-testid={`approvals-approve-${level}`}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] h-7 px-2">
                          {busy === `approve-${level}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <><UserCheck className="w-3 h-3 mr-1" /> Approve</>}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => reject(level)} disabled={busy === `reject-${level}`}
                                data-testid={`approvals-reject-${level}`}
                                className="text-rose-600 border-rose-200 hover:bg-rose-50 text-[11px] h-7 px-2">
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="approvals-audit-card">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold uppercase tracking-wide text-[#2B2D42] mb-3">Audit Log</h3>
            {steps.length === 0 ? (
              <p className="text-xs text-[#6C757D]">No decisions yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {steps.map((s) => (
                  <li key={s.id} className="py-2 text-xs" data-testid={`approvals-audit-${s.id}`}>
                    <div className="flex items-center gap-1.5">
                      {s.decision === 'approved'
                        ? <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                        : <XCircle className="w-3 h-3 text-rose-600" />}
                      <span className="font-semibold uppercase text-[10px]">{s.level}</span>
                      <span className={s.decision === 'approved' ? 'text-emerald-700' : 'text-rose-700'}>
                        {s.decision}
                      </span>
                    </div>
                    <div className="text-[10px] text-[#6C757D] ml-4 mt-0.5">
                      {(s.decided_at || '').slice(0, 19).replace('T', ' ')} · {s.decided_by}
                    </div>
                    {s.note && <div className="ml-4 text-[10px] italic text-[#6C757D]">"{s.note}"</div>}
                  </li>
                ))}
              </ul>
            )}

            {rejected && (
              <div className="mt-3 p-2 rounded-lg bg-rose-50 border border-rose-200 text-[11px] text-rose-700 flex gap-1 items-start">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>This PO has been rejected and cannot receive further approvals.</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default POApprovals;
